import axios from 'axios'
import { createGenericStore } from '@/stores/utils/genericStore.js'

const ChecklistItemTemplatesStoreOptions = {
  state: () => ({
    checklistTemplateId: null,
    initializedChecklistTemplateIds: [],
    addingChecklistTemplateItemIds: [],
    updatingChecklistTemplateItemIds: [],
    deletingChecklistTemplateItemIds: [],
    duplicatingChecklistTemplateItemIds: [],
    bulkDeletingChecklistTemplateIds: [],
  }),

  getters: {
    itemsForChecklistTemplate(state) {
      return (checklistTemplateId) => state.items.filter((itemTemplate) => Number(itemTemplate.checklist_template_id) === Number(checklistTemplateId))
    },
    isAddingChecklistTemplateItem(state) {
      return (checklistTemplateId) => state.addingChecklistTemplateItemIds.includes(Number(checklistTemplateId))
    },
    isUpdatingChecklistTemplateItem(state) {
      return (checklistItemTemplateId) => state.updatingChecklistTemplateItemIds.includes(Number(checklistItemTemplateId))
    },
    isDeletingChecklistTemplateItem(state) {
      return (checklistItemTemplateId) => state.deletingChecklistTemplateItemIds.includes(Number(checklistItemTemplateId))
    },
    isDuplicatingChecklistTemplateItem(state) {
      return (checklistItemTemplateId) => state.duplicatingChecklistTemplateItemIds.includes(Number(checklistItemTemplateId))
    },
    isBulkDeletingChecklistTemplateItems(state) {
      return (checklistTemplateId) => state.bulkDeletingChecklistTemplateIds.includes(Number(checklistTemplateId))
    },
  },

  actions: {
    setChecklistTemplateContext(checklistTemplateId) {
      this.checklistTemplateId = Number(checklistTemplateId)
    },

    async fetchItemsForChecklistTemplate(checklistTemplateId, params = {}) {
      this.setChecklistTemplateContext(checklistTemplateId)

      return this.fetchItems({
        clear: true,
        not_paginated: true,
        checklist_template_id: this.checklistTemplateId,
        order_by: 'order',
        order_direction: 'asc',
        ...params,
      })
    },

    async fetchItemsForChecklistTemplateOnce(checklistTemplateId, params = {}) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)

      if (this.initializedChecklistTemplateIds.includes(normalizedChecklistTemplateId)) {
        return this.itemsForChecklistTemplate(normalizedChecklistTemplateId)
      }

      await this.fetchItemsForChecklistTemplate(normalizedChecklistTemplateId, params)
      this.initializedChecklistTemplateIds.push(normalizedChecklistTemplateId)

      return this.itemsForChecklistTemplate(normalizedChecklistTemplateId)
    },

    async createItemForChecklistTemplate(payload = {}) {
      const normalizedChecklistTemplateId = Number(payload?.checklistTemplateId)
      const instruction = String(payload?.instruction ?? '').trim()
      const details = String(payload?.details ?? '').trim()

      if (normalizedChecklistTemplateId < 1 || instruction.length === 0) {
        throw new Error('A valid checklist template and instruction are required.')
      }

      this.error = null
      this.addingChecklistTemplateItemIds.push(normalizedChecklistTemplateId)

      try {
        await this.fetchItemsForChecklistTemplateOnce(normalizedChecklistTemplateId)

        const existingChecklistTemplateItems = this.itemsForChecklistTemplate(normalizedChecklistTemplateId)
        const nextOrder = existingChecklistTemplateItems.reduce((maxOrder, checklistItemTemplate) => {
          const itemOrder = Number(checklistItemTemplate.order ?? -1)

          return Math.max(maxOrder, itemOrder)
        }, -1) + 1

        const { data: checklistItemTemplate } = await axios.post(this.api, {
          checklist_template_id: normalizedChecklistTemplateId,
          instruction,
          details: details.length > 0 ? details : null,
          order: nextOrder,
        })

        this.itemPatchState(checklistItemTemplate)

        return checklistItemTemplate
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.addingChecklistTemplateItemIds = this.addingChecklistTemplateItemIds.filter((id) => id !== normalizedChecklistTemplateId)
      }
    },

    async updateItem(checklistItemTemplate, payload = {}) {
      const checklistItemTemplateId = Number(checklistItemTemplate?.id)

      if (!checklistItemTemplateId) {
        return null
      }

      this.error = null
      this.updatingChecklistTemplateItemIds.push(checklistItemTemplateId)

      try {
        const { data } = await axios.patch(`${this.api}/${checklistItemTemplateId}`, payload)

        this.itemPatchState(data)

        return data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.updatingChecklistTemplateItemIds = this.updatingChecklistTemplateItemIds.filter((id) => id !== checklistItemTemplateId)
      }
    },

    async updateInstruction(checklistItemTemplate, instruction) {
      const checklistItemTemplateId = Number(checklistItemTemplate?.id)

      if (!checklistItemTemplateId) {
        return null
      }

      const nextInstruction = String(instruction ?? '').trim()
      const currentInstruction = String(checklistItemTemplate?.instruction ?? '').trim()

      if (nextInstruction === currentInstruction) {
        return checklistItemTemplate
      }

      if (nextInstruction.length === 0) {
        throw new Error('An instruction is required for each checklist item template.')
      }

      return this.updateItem(checklistItemTemplate, {
        instruction: nextInstruction,
      })
    },

    async updateDetails(checklistItemTemplate, details) {
      const checklistItemTemplateId = Number(checklistItemTemplate?.id)

      if (!checklistItemTemplateId) {
        return null
      }

      const nextDetails = String(details ?? '').trim()
      const currentDetails = String(checklistItemTemplate?.details ?? '').trim()

      if (nextDetails === currentDetails) {
        return checklistItemTemplate
      }

      return this.updateItem(checklistItemTemplate, {
        details: nextDetails.length > 0 ? details : null,
      })
    },

    async deleteItem(checklistItemTemplateId) {
      const normalizedChecklistItemTemplateId = Number(checklistItemTemplateId)

      if (normalizedChecklistItemTemplateId < 1) {
        return null
      }

      this.error = null
      this.deletingChecklistTemplateItemIds.push(normalizedChecklistItemTemplateId)

      try {
        await this.itemDelete(normalizedChecklistItemTemplateId)
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.deletingChecklistTemplateItemIds = this.deletingChecklistTemplateItemIds.filter((id) => id !== normalizedChecklistItemTemplateId)
      }

      return true
    },

    async duplicateItem(checklistItemTemplate) {
      const normalizedChecklistItemTemplateId = Number(checklistItemTemplate?.id ?? checklistItemTemplate)
      const normalizedChecklistTemplateId = Number(
        checklistItemTemplate?.checklist_template_id ?? this.getItemById(normalizedChecklistItemTemplateId)?.checklist_template_id
      )

      if (normalizedChecklistItemTemplateId < 1 || normalizedChecklistTemplateId < 1) {
        throw new Error('A valid checklist template item is required to duplicate.')
      }

      this.error = null
      this.duplicatingChecklistTemplateItemIds.push(normalizedChecklistItemTemplateId)

      try {
        const { data: duplicatedChecklistItemTemplate } = await axios.post(`${this.api}/${normalizedChecklistItemTemplateId}/duplicate`)

        await this.fetchItemsForChecklistTemplate(normalizedChecklistTemplateId)

        return this.getItemById(duplicatedChecklistItemTemplate.id) ?? duplicatedChecklistItemTemplate
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.duplicatingChecklistTemplateItemIds = this.duplicatingChecklistTemplateItemIds.filter((id) => id !== normalizedChecklistItemTemplateId)
      }
    },

    async bulkDeleteItemsForChecklistTemplate(checklistTemplateId, checklistItemTemplateIds = []) {
      const normalizedChecklistTemplateId = Number(checklistTemplateId)
      const normalizedChecklistItemTemplateIds = [...new Set(
        (Array.isArray(checklistItemTemplateIds) ? checklistItemTemplateIds : [])
          .map((checklistItemTemplateId) => Number(checklistItemTemplateId))
          .filter((checklistItemTemplateId) => checklistItemTemplateId > 0)
      )]

      if (normalizedChecklistTemplateId < 1 || normalizedChecklistItemTemplateIds.length === 0) {
        throw new Error('Select at least one checklist template item to delete.')
      }

      this.error = null
      this.bulkDeletingChecklistTemplateIds.push(normalizedChecklistTemplateId)

      try {
        await axios.post(`${this.api}/bulk-delete`, {
          checklist_template_id: normalizedChecklistTemplateId,
          checklist_item_template_ids: normalizedChecklistItemTemplateIds,
        })

        await this.fetchItemsForChecklistTemplate(normalizedChecklistTemplateId)

        return true
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.bulkDeletingChecklistTemplateIds = this.bulkDeletingChecklistTemplateIds.filter((id) => id !== normalizedChecklistTemplateId)
      }
    },
  },
}

export const useChecklistItemTemplatesStore = createGenericStore(
  'checklistItemTemplates',
  '/api/v1/checklist-item-templates',
  ChecklistItemTemplatesStoreOptions,
)
